
// Pins group only when their 41x58 boxes would touch, with a few pixels of air
const CLUSTER_W = 46;
const CLUSTER_H = 62;
const MAX_ZOOM = 12;
// Ceiling for the opening view, so a tight cluster is not flown into.
const DEFAULT_ZOOM = 5;
// How long the map must rest before pins regroup.
const SETTLE_MS = 250;
// Air between the partner card and the top of its pin.
const CARD_GAP = 12;
// Longest press still read as a tap rather than a hold.
const TAP_MS = 700;
// Furthest a finger may slide and still be read as a tap.
const TAP_SLOP = 24;
// Below this canvas width pins shrink to 30x42, and the cluster radius with them.
const COMPACT_BELOW = 600;
const COMPACT_SCALE = 30 / 41;
const COMPACT_CLUSTER_W = 34;
const COMPACT_CLUSTER_H = 46;
// Curve for a marker's fade when the pins regroup.
const FLY_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
// A wheel this soon after the page scrolled, with the mouse still, is still the page's.
const PAGE_SCROLL_MS = 600;
// A wheel this soon after the last one belongs to the same gesture.
const WHEEL_GAP_MS = 300;

class PartnerMap extends HTMLElement {
  connectedCallback() {
    this.pins = Array.from(this.querySelectorAll('.br-partner-map__pin'));
    if (this.pins.length) this.initStaticPins();

    if (this.querySelector('.br-partner-map__canvas')) this.watchForLiveMap();
  }

  disconnectedCallback() {
    if (this.onKey) document.removeEventListener('keydown', this.onKey);
    if (this.onLiveKey) document.removeEventListener('keydown', this.onLiveKey);
    cancelAnimationFrame(this.glide);
    clearTimeout(this.settleTimer);
    clearTimeout(this.dragMagnet);
    if (this.onPageTap) document.removeEventListener('pointerdown', this.onPageTap, true);
    if (this.onPageScroll) window.removeEventListener('scroll', this.onPageScroll);
    if (this.onOutside) document.removeEventListener('click', this.onOutside);
    if (this.observer) this.observer.disconnect();
    if (this.sizer) this.sizer.disconnect();
    // Only MapLibre has a teardown; a Google map is released with its container.
    if (this.map && typeof this.map.remove === 'function') this.map.remove();
  }

  /* ---------- Static illustration path (no map engine) ---------- */

  initStaticPins() {
    this.pins.forEach((pin) => {
      pin.addEventListener('click', () => {
        const open = pin.getAttribute('aria-expanded') === 'true';
        this.closeAll();
        if (!open) this.openPin(pin);
      });
    });

    this.onKey = (e) => {
      if (e.key !== 'Escape') return;
      const open = this.pins.find((p) => p.getAttribute('aria-expanded') === 'true');
      if (!open) return;
      e.preventDefault();
      this.closeAll();
      open.focus();
    };
    document.addEventListener('keydown', this.onKey);

    this.onOutside = (e) => { if (!this.contains(e.target)) this.closeAll(); };
    document.addEventListener('click', this.onOutside);

    // Escape and an outside click already close a card, but neither is visible.
    this.querySelectorAll('[data-minimize]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const pin = document.getElementById(btn.getAttribute('aria-controls'));
        this.closeAll();
        pin?.focus();
      });
    });
  }

  cardFor(pin) {
    return document.getElementById(pin.getAttribute('aria-controls'));
  }

  openPin(pin) {
    pin.setAttribute('aria-expanded', 'true');
    const card = this.cardFor(pin);
    if (card) card.hidden = false;
  }

  closeAll() {
    this.pins.forEach((pin) => {
      pin.setAttribute('aria-expanded', 'false');
      const card = this.cardFor(pin);
      if (card) card.hidden = true;
    });
  }

  /* ---------- Live map: Google when it is configured, MapLibre otherwise ---------- */

  // Either engine is a few hundred KB, so it is fetched on approach rather than
  // at page load.
  watchForLiveMap() {
    const start = () => { this.observer?.disconnect(); this.initLiveMap(); };

    if (!('IntersectionObserver' in window)) return start();
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) start();
    }, { rootMargin: '400px' });
    this.observer.observe(this);
  }

  announce(message) {
    const live = this.querySelector('.br-partner-map__live');
    if (live) live.textContent = message;
  }

  // Without this a failed engine leaves an empty panel; the text list stays reachable.
  failMap(message, err) {
    console.error('[br-partner-map]', message, err || '');
    this.classList.add('br-partner-map--failed');
    const canvas = this.querySelector('.br-partner-map__canvas');
    if (canvas) canvas.setAttribute('aria-label', 'The map could not be loaded. Every partner is listed as text below.');
    this.announce('The map could not be loaded. Every partner is listed below.');
  }

  async initLiveMap() {
    this.canvasEl = this.querySelector('.br-partner-map__canvas');
    const dataEl = this.querySelector('.br-partner-map__data');
    if (!this.canvasEl || !dataEl) return;

    let partners;
    try {
      partners = JSON.parse(dataEl.textContent);
    } catch (err) {
      return this.failMap('Partner data could not be parsed.', err);
    }

    const valid = partners
      .map((p) => ({ ...p, lat: parseFloat(p.lat), lng: parseFloat(p.lng) }))
      .filter((p) => !Number.isNaN(p.lat) && !Number.isNaN(p.lng));
    if (!valid.length) return;

    // Two ways to answer a tap, chosen in the theme editor
    this.listMode = this.dataset.detailStyle === 'list';
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Escape unwinds one layer at a time: card, then fan. In list mode there is
    // only ever the panel.
    this.onLiveKey = (e) => {
      if (e.key !== 'Escape') return;
      if (this.panelOpen) {
        e.preventDefault();
        const trigger = this.panelTrigger;
        this.closePanel();
        trigger?.focus();
        return;
      }
      if (this.popup) {
        e.preventDefault();
        const trigger = this.popupTrigger;
        this.closePopup();
        trigger?.focus();
        return;
      }
      const fan = this.openFan || this.spreadFans?.find((w) => w.contains(document.activeElement))
        || this.spreadFans?.[0];
      if (fan) {
        e.preventDefault();
        const toggle = fan.querySelector('.br-partner-map__gcluster');
        this.collapseFan();
        toggle?.focus();
      }
    };
    document.addEventListener('keydown', this.onLiveKey);
    this.addEventListener('pointerdown', () => { this.lastInput = 'pointer'; clearTimeout(this.dragMagnet); }, true);
    this.addEventListener('keydown', () => { this.lastInput = 'key'; }, true);
    this.guardWheel();

    if (this.dataset.googleKey && this.dataset.mapId) return this.initGoogle(valid);
    return this.initMapLibre(valid);
  }

  /* ---------- Google Maps ---------- */

  async initGoogle(partners) {
    // A rejected key or referrer does not throw: Google paints its own grey
    // panel and calls this instead. Without it the section would sit there
    // looking broken with the text list still hidden.
    window.gm_authFailure = () => {
      this.fallBack(partners, 'Google Maps rejected the API key or this domain. Check the key restrictions in Cloud Console.');
    };
    this.canvasBlank = this.canvasEl.cloneNode(false);

    try {
      await loadGoogleMaps(this.dataset.googleKey);
    } catch (err) {
      return this.fallBack(partners, 'The Google Maps script could not be loaded.', err);
    }

    this.engine = 'google';
    this.classList.add('br-partner-map--google');

    let map;
    try {
      map = new google.maps.Map(this.canvasEl, {
        // Required for advanced markers, and the only way to style the base
        // map — MapOptions.styles is ignored whenever a map ID is set.
        mapId: this.dataset.mapId,
        center: { lat: 45, lng: -95 },
        zoom: 3,
        minZoom: 2,
        maxZoom: MAX_ZOOM,
        gestureHandling: 'greedy',
        clickableIcons: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        rotateControl: false,
        zoomControl: false,
      });
    } catch (err) {
      return this.fallBack(partners, 'The map could not be created.', err);
    }

    this.map = map;
    this.markers = [];
    this.markerEls = [];
    this.spreadFans = [];
    // Google's own projection: a raster map draws a rounder zoom than getZoom() reports
    this.projector = Object.assign(new google.maps.OverlayView(), {
      onAdd() {}, draw() {}, onRemove() {},
    });
    this.projector.setMap(map);

    google.maps.event.addListenerOnce(map, 'idle', () => {
      if (this.fellBack) return;
      this.measureCompact();
      this.frame(partners);
      this.render(partners);
      // Re-clustering while a card or a fan is open would detach what it anchors to.
      // Pins ride the map while it moves and regroup once it has rested, as Airbnb's do.
      map.addListener('idle', () => { this.settle(partners); this.updateShowAll(); });
      map.addListener('bounds_changed', () => clearTimeout(this.settleTimer));
      map.addListener('click', () => this.dismiss());
      map.addListener('dragstart', () => { this.stopGlide(); if (this.popup) this.closePopup(); });
      this.addShowAll(partners);
      this.addGoogleZoom();
      map.addListener('dragend', () => { this.userMoved = true; this.afterDrag(); });
      map.addListener('zoom_changed', () => {
        if (!this.gliding && !this.centering) this.releaseForZoom();
        this.updateGoogleZoom();
      });
      this.announce(`Map ready. ${partners.length} partners shown.`);
      this.watchWidth(partners);
    });
  }

  // Anything Google refuses, such as the editor preview's domain, gets MapLibre.
  fallBack(partners, message, err) {
    if (this.fellBack || !this.dataset.maplibreJs) return this.failMap(message, err);
    this.fellBack = true;
    console.warn('[br-partner-map]', message, 'Showing the MapLibre map instead.', err || '');
    this.sizer?.disconnect();
    this.closePopup();
    this.classList.remove('br-partner-map--google');
    const fresh = this.canvasBlank.cloneNode(false);
    this.canvasEl.replaceWith(fresh);
    this.canvasEl = fresh;
    this.map = null;
    this.markerByKey = null;
    this.renderStamp = null;
    return this.initMapLibre(partners);
  }

  /* ---------- MapLibre (used when no Google key is set) ---------- */

  async initMapLibre(partners) {
    try {
      await loadMapLibre(this.dataset.maplibreJs, this.dataset.maplibreCss);
    } catch (err) {
      return this.failMap('The map engine could not be loaded.', err);
    }

    this.engine = 'maplibre';
    let map;
    try {
      map = new maplibregl.Map({
        container: this.canvasEl,
        style: brandStyle(this),
        center: [-95, 45],
        zoom: 2.6,
        minZoom: 0.5,
        maxZoom: MAX_ZOOM,
        attributionControl: { compact: true, customAttribution: 'Boundaries: Natural Earth' },
        dragRotate: false,
        pitchWithRotate: false,
        touchZoomRotate: true,
        cooperativeGestures: false,
      });
    } catch (err) {
      return this.failMap('The map could not be created.', err);
    }

    map.touchZoomRotate?.disableRotation();
    // The + and − buttons start at once and land in ~330ms, like Google's.
    ['zoomIn', 'zoomOut'].forEach((name) => {
      const step = map[name].bind(map);
      map[name] = (options, data) => {
        const back = name === 'zoomOut' && this.jumpBack();
        if (back) return this.flyBack(back);
        const pin = this.anchorPin(this.canvasEl.clientWidth / 2, this.canvasEl.clientHeight / 2);
        const around = pin ? [pin.lng, pin.lat] : undefined;
        step({ duration: this.reduceMotion ? 0 : 330, easing: EASE, around, ...options }, data);
      };
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    this.map = map;
    this.markers = [];
    this.markerEls = [];
    this.spreadFans = [];

    map.on('error', (e) => console.warn('[br-partner-map] style error:', e && e.error));

    map.on('load', () => {
      this.addCityLabels(map);
      this.measureCompact();
      this.frame(partners);
      this.render(partners);
      map.on('moveend', () => {
        if (this.busy() || this.magnet()) return;
        this.render(partners);
        // render() skips the rebuild on a plain pan, but the labels it hides
        // are chosen from where the pins now project, so they refresh anyway.
        this.refreshCityLabels();
      });
      this.announce(`Map ready. ${partners.length} partners shown.`);
      this.watchWidth(partners);
      map.on('dragstart', () => { if (this.popup) this.closePopup(); });
      map.on('moveend', () => this.updateShowAll());
      this.addShowAll(partners);
      map.on('dragend', () => { this.userMoved = true; this.afterDrag(); });
      map.on('zoomend', (e) => { if (e.originalEvent) this.userMoved = true; });
      // A visitor's own zoom re-clusters, so whatever is open closes first.
      map.on('zoomstart', (e) => { if (e.originalEvent) this.releaseForZoom(); });
    });
  }

  /* ---------- Engine-neutral view state ---------- */

  // A mouse wheel zooms only once it starts on the map; touch drags with one finger.
  wheelFree() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  // The wheel zooms only when it starts on the map; otherwise the page scrolls on.
  // On the map it zooms around the nearest pin, so a cluster opens in place.
  guardWheel() {
    let pageAt = 0; let mapAt = 0; let movedAt = 0; let x = 0; let y = 0; let pin = null;
    const relayed = new WeakSet();
    this.onPageScroll = () => { pageAt = performance.now(); };
    window.addEventListener('scroll', this.onPageScroll, { passive: true });
    this.addEventListener('pointermove', (e) => {
      if (Math.abs(e.clientX - x) + Math.abs(e.clientY - y) > 2) movedAt = performance.now();
      x = e.clientX; y = e.clientY;
    }, { passive: true });
    this.addEventListener('wheel', (e) => {
      if (relayed.has(e) || !this.map || !this.wheelFree()) return;
      if (!this.canvasEl.contains(e.target)) return;
      const now = performance.now();
      const sameGesture = now - mapAt < WHEEL_GAP_MS;
      const passingOver = now - pageAt < PAGE_SCROLL_MS && movedAt < pageAt;
      const atFloor = e.deltaY > 0 && this.floorZoom !== undefined && this.zoom() <= this.floorZoom + 0.01;
      if (!e.ctrlKey && !sameGesture && (passingOver || atFloor)) {
        e.stopPropagation();
        return;
      }
      mapAt = now;
      clearTimeout(this.dragMagnet);
      if (this.gliding) this.stopGlide();
      const box = this.canvasEl.getBoundingClientRect();
      if (!sameGesture) pin = this.anchorPin(e.clientX - box.left, e.clientY - box.top);
      const at = pin && this.screenOf(pin);
      if (!at || Math.hypot(box.left + at.x - e.clientX, box.top + at.y - e.clientY) < 2) return;
      e.preventDefault();
      e.stopPropagation();
      const copy = new WheelEvent('wheel', {
        bubbles: true, cancelable: true, composed: true, view: window,
        clientX: box.left + at.x, clientY: box.top + at.y,
        screenX: e.screenX + box.left + at.x - e.clientX, screenY: e.screenY + box.top + at.y - e.clientY,
        deltaX: e.deltaX, deltaY: e.deltaY, deltaZ: e.deltaZ, deltaMode: e.deltaMode,
        ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, metaKey: e.metaKey,
      });
      relayed.add(copy);
      e.target.dispatchEvent(copy);
    }, { capture: true, passive: false });
  }

  // The partner on screen closest to a point in the canvas, or null if none shows.
  anchorPin(px, py) {
    if (!this.allPartners?.length) return null;
    const w = this.canvasEl.clientWidth; const h = this.canvasEl.clientHeight;
    let near = null; let best = Infinity;
    for (const p of this.allPartners) {
      const at = this.screenOf(p);
      if (at.x < 0 || at.x > w || at.y < 0 || at.y > h) continue;
      const d = Math.hypot(at.x - px, at.y - py);
      if (d < best) { best = d; near = p; }
    }
    return near;
  }

  // Google's own zoom control can't be told where to zoom, so these replace it.
  addGoogleZoom() {
    const group = document.createElement('div');
    group.className = 'maplibregl-ctrl maplibregl-ctrl-group br-partner-map__zoom';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', 'Map zoom');
    const make = (dir, label) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `maplibregl-ctrl-zoom-${dir}`;
      btn.setAttribute('aria-label', label);
      btn.innerHTML = '<span class="maplibregl-ctrl-icon" aria-hidden="true"></span>';
      btn.addEventListener('click', () => this.zoomGoogle(dir === 'in' ? 1 : -1));
      group.append(btn);
      return btn;
    };
    this.zoomBtns = { in: make('in', 'Zoom in'), out: make('out', 'Zoom out') };
    this.map.controls[google.maps.ControlPosition.RIGHT_TOP].push(group);
    this.updateGoogleZoom();
  }

  updateGoogleZoom() {
    if (!this.zoomBtns) return;
    const z = this.zoom();
    const floor = this.floorZoom ?? 2;
    const set = (btn, off) => { btn.disabled = off; btn.setAttribute('aria-disabled', String(off)); };
    set(this.zoomBtns.in, z >= MAX_ZOOM - 0.01);
    set(this.zoomBtns.out, z <= floor + 0.01);
  }

  // One step in or out, holding the pin nearest the middle still on screen.
  zoomGoogle(step) {
    const back = step < 0 && !this.gliding && this.jumpBack();
    if (back) return this.flyBack(back);
    const from = this.gliding ? this.glideTarget : null;
    const z0 = from ? from.zoom : this.zoom();
    const z1 = Math.max(this.floorZoom ?? 2, Math.min(MAX_ZOOM, Math.round(z0) + step));
    if (z1 === z0) return;
    this.releaseForZoom();
    const c = this.map.getCenter();
    const pin = this.anchorPin(this.canvasEl.clientWidth / 2, this.canvasEl.clientHeight / 2);
    let view = { lat: c.lat(), lng: c.lng(), zoom: z1 };
    if (pin && !from) {
      const S = 2 ** (z1 - this.zoom());
      const dLng = ((((c.lng() - pin.lng) % 360) + 540) % 360) - 180;
      const y = mercatorY(pin.lat) + (mercatorY(c.lat()) - mercatorY(pin.lat)) / S;
      view = { lat: mercatorLat(y), lng: pin.lng + dLng / S, zoom: z1 };
    } else if (from) {
      view = { ...from, zoom: z1 };
    }
    this.userMoved = true;
    this.glideGoogle(view, this.reduceMotion ? 0 : 330);
  }

  // One click back to the opening view, however far in the visitor has gone.
  addShowAll(partners) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'br-partner-map__show-all';
    btn.textContent = 'Show all partners';
    btn.hidden = true;
    btn.addEventListener('click', () => this.showAll(partners));
    this.canvasEl.after(btn);
    this.showAllBtn = btn;
  }

  updateShowAll() {
    const btn = this.showAllBtn;
    if (!btn || this.homeZoom === undefined) return;
    const away = this.zoom() > this.homeZoom + 0.5 || this.driftedFromHome();
    if (!away && btn === document.activeElement) return;
    btn.hidden = !away;
  }

  // Panned far enough that the opening view's centre is a third of the canvas away.
  driftedFromHome() {
    if (!this.home) return false;
    const box = this.canvasEl.getBoundingClientRect();
    const c = this.map.getCenter();
    const lat = this.engine === 'google' ? c.lat() : c.lat;
    const lng = this.engine === 'google' ? c.lng() : c.lng;
    const world = this.tileSize() * 2 ** this.zoom();
    // Google can report a longitude a whole turn round the globe (250.9 for −109.1).
    const turn = ((((lng - this.home.lng) % 360) + 540) % 360) - 180;
    const dx = (turn / 360) * world;
    const dy = ((mercatorY(this.home.lat) - mercatorY(lat)) / (2 * Math.PI)) * world;
    return Math.abs(dx) > box.width / 3 || Math.abs(dy) > box.height / 3;
  }

  // A cluster tap jumps several levels, so one zoom-out takes it back in one move
  rememberJump(to) {
    const c = this.map.getCenter();
    const lng = this.engine === 'google' ? c.lng() : c.lng;
    this.jumps = (this.jumps || []).concat({ from: { lat: this.centreLat(), lng, zoom: this.zoom() }, to });
  }

  // The view before the last cluster jump, if the camera still sits where it landed
  jumpBack() {
    const z = this.zoom();
    const trail = this.jumps || [];
    while (trail.length && z < trail[trail.length - 1].to - 0.3) trail.pop();
    const last = trail[trail.length - 1];
    if (!last || Math.abs(z - last.to) > 0.3) return null;
    trail.pop();
    return last.from;
  }

  flyBack(view) {
    this.releaseForZoom();
    const steps = Math.abs(view.zoom - this.zoom());
    const duration = this.reduceMotion ? 0 : Math.round(Math.min(900, 400 + 130 * Math.max(0, steps - 1)));
    this.userMoved = true;
    if (this.engine === 'google') this.glideGoogle(view, duration);
    else this.map.easeTo({ center: [view.lng, view.lat], zoom: view.zoom, duration, easing: EASE });
    this.announce('Zoomed back out.');
  }

  showAll(partners) {
    this.jumps = [];
    this.closePanel();
    this.closePopup();
    this.collapseFan();
    const view = this.frameView(this.openingSet(partners));
    const steps = Math.abs(view.zoom - this.zoom());
    const duration = this.reduceMotion ? 0 : Math.round(Math.min(900, 400 + 130 * Math.max(0, steps - 1)));
    const landed = () => {
      this.userMoved = false;
      // The button hides at home, so focus moves to the map it just reset.
      this.canvasEl.tabIndex = -1;
      this.canvasEl.focus({ preventScroll: true });
      this.showAllBtn.hidden = true;
    };
    if (this.engine === 'google') {
      this.glideGoogle(view, duration, landed);
    } else {
      this.map.once('moveend', () => setTimeout(landed, 0));
      this.map.easeTo({ center: [view.lng, view.lat], zoom: view.zoom, duration, easing: EASE });
    }
    this.announce(`Showing all ${partners.length} partners.`);
  }

  settle(partners) {
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => { if (!this.busy() && !this.magnet()) this.render(partners); }, SETTLE_MS);
  }

  // A zoom-in that lands on empty map glides on to the nearest partner.
  magnet() {
    const z = this.zoom();
    const zoomedIn = this.restZoom !== undefined && z > this.restZoom + 0.1;
    this.restZoom = z;
    const near = zoomedIn && this.allPartners?.length ? this.emptyNear() : null;
    if (near) this.pullTo(near);
    return !!near;
  }

  // A drag waits a moment first: the visitor may still be crossing the ocean.
  afterDrag() {
    clearTimeout(this.dragMagnet);
    const check = () => {
      if (this.busy() || this.map.isMoving?.()) { this.dragMagnet = setTimeout(check, 200); return; }
      const near = this.emptyNear();
      if (near) this.pullTo(near);
    };
    this.dragMagnet = setTimeout(check, 900);
  }

  // The partner nearest the middle when no partner sits well inside the view; else null.
  emptyNear() {
    const box = this.canvasEl.getBoundingClientRect();
    const mx = box.width * 0.12; const my = box.height * 0.12;
    let near = null; let best = Infinity;
    for (const p of this.allPartners) {
      const at = this.screenOf(p);
      if (at.x > mx && at.x < box.width - mx && at.y > my && at.y < box.height - my) return null;
      const d = Math.hypot(at.x - box.width / 2, at.y - box.height / 2);
      if (d < best) { best = d; near = p; }
    }
    return near;
  }

  pullTo(near) {
    const duration = this.reduceMotion ? 0 : 420;
    this.userMoved = true;
    if (this.engine === 'google') this.glideGoogle({ lat: near.lat, lng: near.lng, zoom: this.zoom() }, duration);
    else this.map.easeTo({ center: [near.lng, near.lat], duration, easing: EASE });
    this.announce(`Moved to the nearest partner, ${near.name} in ${near.city}.`);
  }

  zoom() {
    return this.map.getZoom();
  }

  centreLat() {
    const c = this.map.getCenter();
    return this.engine === 'google' ? c.lat() : c.lat;
  }

  // The map is built when it scrolls into view, so the frame is computed against
  // whatever the canvas measured at that moment. Nothing re-framed it afterwards,
  // which left coastal pins cut off the edge after any width change. Width-guarded:
  // fitBounds moves the canvas height, which would otherwise feed straight back in.
  watchWidth(partners) {
    if (!('ResizeObserver' in window)) return;
    let last = this.canvasEl.getBoundingClientRect().width;
    this.sizer = new ResizeObserver(() => {
      const now = this.canvasEl.getBoundingClientRect().width;
      if (Math.abs(now - last) < 1) return;
      last = now;
      if (this.measureCompact()) {
        this.renderStamp = null;
        if (!this.busy()) this.render(partners);
      }
      // A visitor who has panned or zoomed chose that view; only re-frame an untouched map.
      if (this.busy() || this.userMoved) return;
      if (this.engine === 'maplibre') this.map.resize();
      this.frame(partners);
    });
    this.sizer.observe(this);
  }

  // MapLibre's zoom is defined against 512px tiles, Google's against 256px.
  tileSize() {
    return this.engine === 'google' ? 256 : 512;
  }

  // The pin box in screen pixels, plus the world's width at this zoom on this engine
  clusterBox(zoom) {
    return this.compact
      ? { w: COMPACT_CLUSTER_W, h: COMPACT_CLUSTER_H, world: this.tileSize() * 2 ** zoom }
      : { w: CLUSTER_W, h: CLUSTER_H, world: this.tileSize() * 2 ** zoom };
  }

  pinScale() {
    return this.compact ? COMPACT_SCALE : 1;
  }

  // Phone-sized canvas: smaller pins. Returns true when the size class changed.
  measureCompact() {
    const was = this.compact;
    this.compact = this.canvasEl.getBoundingClientRect().width < COMPACT_BELOW;
    this.classList.toggle('br-partner-map--compact', this.compact);
    return was !== undefined && was !== this.compact;
  }

  // Phone opening view skips far outliers (Hawaii) that cost half a zoom level or more.
  openingSet(partners) {
    if (!this.compact || partners.length < 5) return partners;
    let set = partners;
    for (let i = 0; i < 2; i += 1) {
      const base = this.frameView(set, MAX_ZOOM).zoom;
      let best = null;
      set.forEach((p) => {
        const rest = set.filter((q) => q !== p);
        const gain = this.frameView(rest, MAX_ZOOM).zoom - base;
        if (gain >= 0.5 && (!best || gain > best.gain)) best = { rest, gain };
      });
      if (!best) break;
      set = best.rest;
    }
    return set;
  }

  // Centre and zoom that sit `main` inside the canvas with a margin to spare.
  // Computed, not fitBounds
  frameView(main, ceiling = DEFAULT_ZOOM) {
    const box = this.canvasEl.getBoundingClientRect();
    // A phone map needs every pixel for spreading pins.
    const pad = this.compact ? 22 : Math.max(30, Math.min(70, Math.min(box.width, box.height) * 0.12));
    const w = Math.max(1, box.width - pad * 2);
    const h = Math.max(1, box.height - pad * 2);
    const size = this.tileSize();

    const lngs = main.map((p) => p.lng);
    const lats = main.map((p) => p.lat);
    const west = Math.min(...lngs);
    const east = Math.max(...lngs);
    const y0 = mercatorY(Math.min(...lats));
    const y1 = mercatorY(Math.max(...lats));

    // A lone partner, or a set sharing one coordinate, has no span to fit.
    const lngSpan = east - west;
    const ySpan = y1 - y0;
    const zx = lngSpan > 1e-6 ? Math.log2((w * 360) / (size * lngSpan)) : ceiling;
    const zy = ySpan > 1e-9 ? Math.log2((h * 2 * Math.PI) / (size * ySpan)) : ceiling;

    return {
      lat: mercatorLat((y0 + y1) / 2),
      lng: (west + east) / 2,
      zoom: Math.min(zx, zy, ceiling),
    };
  }

  // Framed on every partner, Hawaii included
  frame(partners) {
    if (!partners.length) return;
    const view = this.frameView(this.openingSet(partners));
    this.homeZoom = view.zoom;
    this.restZoom = view.zoom;
    this.home = view;

    // Camera first: a fence raised around the old view can pin the map before it moves.
    if (this.engine === 'google') {
      this.map.setOptions({ restriction: null, minZoom: 2 });
      this.map.setCenter({ lat: view.lat, lng: view.lng });
      this.map.setZoom(view.zoom);
    } else {
      this.map.setMaxBounds(null);
      this.map.jumpTo({ center: [view.lng, view.lat], zoom: view.zoom });
    }
    this.fence(view, partners);
  }

  // Zoom out only a little past the opening view, and pan only about one view
  // beyond it, so the map never drifts into an empty ocean or a repeated world.
  fence(view, partners) {
    const box = this.canvasEl.getBoundingClientRect();
    const world = this.tileSize() * 2 ** view.zoom;
    const halfLng = (box.width / 2 / world) * 360 * 2;
    const halfY = (box.height / 2 / world) * 2 * Math.PI * 2;
    const y = mercatorY(view.lat);
    const lngs = partners.map((p) => p.lng);
    const lats = partners.map((p) => p.lat);
    const pad = halfLng / 4;
    // Just inside ±180: Google reads an edge on the antimeridian as the far side.
    const west = Math.max(-179.9, Math.min(view.lng - halfLng, Math.min(...lngs) - pad));
    const east = Math.min(179.9, Math.max(view.lng + halfLng, Math.max(...lngs) + pad));
    const south = Math.max(-85, Math.min(mercatorLat(y - halfY), Math.min(...lats) - pad));
    const north = Math.min(85, Math.max(mercatorLat(y + halfY), Math.max(...lats) + pad));
    const floor = view.zoom - 0.5;
    this.floorZoom = this.engine === 'google' ? Math.ceil(floor) : floor;

    if (this.engine === 'google') {
      this.map.setOptions({
        minZoom: floor,
        // Strict, so the view's edges stop at the fence, as MapLibre's maxBounds does.
        restriction: { latLngBounds: { west, east, south, north }, strictBounds: true },
      });
      return;
    }
    this.map.setMinZoom(floor);
    this.map.setMaxBounds([[west, south], [east, north]]);
  }

  /* ---------- Markers ---------- */

  // Anything open that a re-cluster would rip out from under the visitor.
  busy() {
    return Boolean(this.panelOpen || this.popup || this.openFan || this.spreadFans.length
      || this.gliding || this.centering);
  }

  releaseForZoom() {
    if (this.openFan || this.spreadFans.length) this.collapseFan();
    if (this.popup) this.closePopup();
  }

  // A click on the map itself puts everything away.
  dismiss() {
    if (this.panelOpen) return this.closePanel();
    if (this.popup) {
      const trigger = this.popupTrigger;
      this.closePopup();
      trigger?.focus();
      return undefined;
    }
    return undefined;
  }

  // The one place that decides which surface answers a tap.
  openDetail(partners, trigger) {
    if (this.listMode) return this.openPanel(partners, trigger);
    return this.openPartner(partners[0], trigger);
  }

  render(partners) {
    // Kept for splitZoom: a tapped cluster is judged against every partner,
    // not just its own members.
    this.allPartners = partners;
    this.groupCache = new Map();
    const zoom = this.zoom();
    const lat = this.centreLat();
    // Clustering only depends on zoom and the centre latitude, so a plain pan
    // must not tear down and rebuild every marker.
    const stamp = `${zoom.toFixed(2)}|${Math.round(lat)}`;
    if (stamp === this.renderStamp) return;
    const groups = clusterByProximity(partners, this.clusterBox(zoom));

    this.renderStamp = stamp;

    // Only what changed is touched. Rebuilding the field wholesale made every
    // marker replay its entrance the instant the camera settled — thirteen of
    // fifteen, five of them identical to the ones they replaced, which read as
    // a blink at exactly the moment the move should have come to rest.
    const live = this.markerByKey || new Map();
    const next = new Map();
    // Where every outgoing marker sits now, read before anything moves.
    live.forEach((m) => { m.at = tipOf(m.el); });
    const added = [];

    groups.forEach((cluster) => {
      const key = clusterKey(cluster);
      const kept = live.get(key);
      if (kept) {
        next.set(key, kept);
        live.delete(key);
        return;
      }
      const el = cluster.items.length === 1
        ? this.buildPin(cluster.items[0])
        : this.buildCluster(cluster);
      const entry = { el, members: cluster.items.map(memberId), spot: { lat: cluster.lat, lng: cluster.lng } };
      el.style.opacity = '0';
      entry.marker = this.placeMarker(el, cluster.lat, cluster.lng);
      next.set(key, entry);
      added.push(entry);
    });

    const gone = [...live.values()];
    this.markerByKey = next;
    this.indexMarkers();
    this.refreshCityLabels();
    this.flyField(added, gone);
  }

  // Pins fly out of the cluster they left, or into the one that now holds them.
  flyField(added, gone) {
    const holds = (outer, inner) => inner.every((id) => outer.includes(id));
    gone.forEach((m) => {
      m.members = m.members || (m.el.__members || []).map(memberId);
      m.el.inert = true;
      m.el.setAttribute('aria-hidden', 'true');
    });

    // Two frames: Google places marker content a frame after it is added.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      added.forEach((m) => {
        const from = gone.find((g) => g.members.length > m.members.length && holds(g.members, m.members));
        const to = tipOf(m.el);
        const dx = from && from.at && to ? from.at.x - to.x : 0;
        const dy = from && from.at && to ? from.at.y - to.y : 0;
        m.el.style.opacity = '';
        this.fly(m.el, dx, dy, 'in');
      });

      gone.forEach((m) => {
        const into = added.find((a) => a.members.length > m.members.length && holds(a.members, m.members));
        const to = into && tipOf(into.el);
        const dx = to && m.at ? to.x - m.at.x : 0;
        const dy = to && m.at ? to.y - m.at.y : 0;
        const anim = this.fly(m.el, dx, dy, 'out');
        const drop = () => this.dropMarker(m.marker);
        if (anim) anim.finished.then(drop, drop);
        else drop();
      });
    }));
  }

  // Regrouping happens after the map has stopped, so pins fade in place, as
  // Airbnb's do; scaling would skew the pin boxes the city labels are hidden by.
  fly(el, dx, dy, dir) {
    if (typeof el.animate !== 'function') return null;
    const away = { opacity: 0 };
    const home = { opacity: 1 };
    return el.animate(dir === 'in' ? [away, home] : [home, away], {
      duration: dir === 'in' ? 180 : 140,
      easing: FLY_EASE,
      fill: dir === 'out' ? 'forwards' : 'none',
    });
  }

  // The flat lists the rest of the file works from, derived from the DOM rather
  // than bookkept, so a kept fan's members are never lost from the tab order.
  indexMarkers() {
    const entries = [...this.markerByKey.values()];
    this.markers = entries.map((m) => m.marker);
    this.markerEls = entries.flatMap(({ el }) => (
      el.matches('.br-partner-map__gpin, .br-partner-map__gcluster')
        ? [el]
        : [...el.querySelectorAll('.br-partner-map__gpin, .br-partner-map__gcluster')]
    ));
  }

  dropMarker(marker) {
    if (this.engine === 'google') marker.map = null;
    else marker.remove();
  }

  clearMarkers() {
    this.closePanel();
    this.closePopup();
    this.openFan = null;
    this.groupCache = new Map();
    (this.markerByKey || new Map()).forEach((m) => this.dropMarker(m.marker));
    this.markerByKey = new Map();
    this.renderStamp = null;
    this.markers = [];
    this.markerEls = [];
    this.spreadFans = [];
  }

  placeMarker(el, lat, lng) {
    if (this.engine === 'google') {
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        position: { lat, lng },
        content: el,
      });
      el.__marker = marker;
      return marker;
    }
    const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([lng, lat])
      .addTo(this.map);
    el.__marker = marker;
    return marker;
  }

  buildPin(partner) {
    return this.pinButton(partner);
  }

  // Every touch tap is answered on touchend: a drifting finger loses its click
  // to the browser or to the map's one-finger drag
  bindTap(btn, run) {
    btn.addEventListener('pointerdown', (e) => {
      this.tapGesture = e.pointerType === 'touch'
        ? { at: Date.now(), y: window.scrollY, x0: e.clientX, y0: e.clientY }
        : null;
    });

    btn.addEventListener('touchend', (e) => {
      const tap = this.tapGesture;
      this.tapGesture = null;
      if (!tap || Date.now() - tap.at > TAP_MS || window.scrollY !== tap.y) return;
      const lift = e.changedTouches && e.changedTouches[0];
      if (!lift || Math.hypot(lift.clientX - tap.x0, lift.clientY - tap.y0) > TAP_SLOP) return;
      // Stops the browser's own click, so the tap is answered once
      if (e.cancelable) e.preventDefault();
      e.stopPropagation();
      // A camera move started inside touchend is cancelled by the engine's cleanup.
      setTimeout(() => run(e), 0);
    });

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      run(e);
    });
  }

  pinButton(partner, className = 'br-partner-map__gpin') {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = className;
    btn.setAttribute('aria-label', `${partner.name}, ${partner.city}`);
    btn.setAttribute('aria-expanded', 'false');
    btn.__partner = partner;
    if (this.listMode) btn.setAttribute('aria-controls', this.panelId());
    btn.appendChild(this.pinImage(this.dataset.pinUrl));
    this.bindTap(btn, () => this.openDetail([partner], btn));
    return btn;
  }

  pinImage(src) {
    const img = document.createElement('img');
    img.src = src || '';
    img.alt = '';
    img.width = 41;
    img.height = 58;
    img.decoding = 'async';
    return img;
  }

  // List mode lists. Card mode zooms to the nearest level that breaks this
  // cluster up, and only fans when no zoom ever could.
  buildCluster(cluster, { fan = false } = {}) {
    const names = cluster.items.map((p) => p.name).join(', ');
    // Asked of MAX_ZOOM rather than of the zoom we happen to be at, so the
    // label cannot go stale, and so partners that are distinct but closer than
    // a pin is wide at full zoom are answered honestly: nothing will ever part
    // them, so they open in place. The zoom itself is worked out on the tap.
    const canZoom = !fan && !this.listMode && this.partsAtMaxZoom(cluster);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'br-partner-map__gcluster';
    btn.setAttribute(
      'aria-label',
      canZoom
        ? `Zoom in to ${cluster.items.length} partners in this area: ${names}`
        : `Show ${cluster.items.length} partners in this area: ${names}`
    );
    btn.setAttribute('aria-expanded', 'false');
    btn.__members = cluster.items;
    btn.appendChild(this.pinImage(this.dataset.clusterPinUrl));

    const count = document.createElement('span');
    count.className = 'br-partner-map__gcluster-count';
    count.textContent = String(cluster.items.length);
    count.setAttribute('aria-hidden', 'true');
    btn.appendChild(count);

    if (this.listMode) {
      btn.setAttribute('aria-controls', this.panelId());
      this.bindTap(btn, () => this.openPanel(cluster.items, btn));
      return btn;
    }

    if (canZoom) {
      btn.removeAttribute('aria-expanded');
      // What this pin stands for, so a test never has to parse it back out of
      // the label — one partner's name contains a comma.
      btn.__cluster = cluster;
      this.bindTap(btn, () => this.openCluster(cluster));
      return btn;
    }

    // Identical coordinates: no zoom will ever part them, so they part in place.
    return this.buildFan(cluster, btn);
  }

  /* ---------- Card mode: the zoom that breaks a cluster up ---------- */

  // Can the furthest zoom part them at all? If not, no zoom will.
  partsAtMaxZoom(cluster) {
    if (!this.allPartners || !this.allPartners.length) return false;
    const tapped = new Set(cluster.items.map(memberId));
    return !this.groupsAt(MAX_ZOOM, cluster.lat).some((g) => (
      g.items.length === tapped.size && g.items.every((p) => tapped.has(memberId(p)))
    ));
  }

  // Every cluster asks the same question of the same partner list, so one
  // render pass groups them once per zoom and latitude band.
  groupsAt(zoom, lat) {
    const key = `${zoom}|${Math.round(lat)}`;
    if (!this.groupCache) this.groupCache = new Map();
    if (!this.groupCache.has(key)) {
      this.groupCache.set(key,
        clusterByProximity(this.allPartners, this.clusterBox(zoom)));
    }
    return this.groupCache.get(key);
  }

  // The nearest zoom at which this cluster is no longer one group — the least
  // the camera can move and still have the tapped pin gone when it lands.
  // null when no zoom ever separates them.
  splitZoom(cluster) {
    if (!this.allPartners || !this.allPartners.length) return null;

    const id = (p) => `${p.name}|${p.lat}|${p.lng}`;
    const tapped = new Set(cluster.items.map(id));

    for (let z = Math.floor(this.zoom()) + 1; z <= MAX_ZOOM; z += 1) {
      const intact = this.groupsAt(z, cluster.lat).some((g) => (
        g.items.length === tapped.size && g.items.every((p) => tapped.has(id(p)))
      ));
      if (!intact) return z;
    }
    return null;
  }

  // The first zoom at which every member of this cluster stands on its own pin,
  // or MAX_ZOOM when some of them never can.
  separationZoom(cluster) {
    const tapped = new Set(cluster.items.map(memberId));
    for (let z = Math.floor(this.zoom()) + 1; z <= MAX_ZOOM; z += 1) {
      const biggest = this.groupsAt(z, cluster.lat)
        .filter((g) => g.items.some((p) => tapped.has(memberId(p))))
        .reduce((most, g) => Math.max(most, g.items.filter((p) => tapped.has(memberId(p))).length), 0);
      if (biggest <= 1) return z;
    }
    return MAX_ZOOM;
  }

  // Where a tap should take the camera: far enough to frame the members of the
  // cluster, never further than parting them needs.
  //
  // Stopping at splitZoom — the first level where the group is no longer one
  // group — satisfies the letter of "the pin you clicked is gone" and fails the
  // point of it. On the real directory the opening view's 22-cluster became a
  // *12* in the same place, which reads as the same cluster still sitting
  // there, and is why clicking felt like it did nothing. Framing the members
  // instead takes that 22 to a largest remaining piece of 4 in one tap.
  openView(cluster) {
    const fit = this.frameView(cluster.items, MAX_ZOOM);
    const goal = Math.min(fit.zoom, this.separationZoom(cluster), MAX_ZOOM);
    const least = this.splitZoom(cluster);
    return {
      lat: fit.lat,
      lng: fit.lng,
      zoom: least === null ? goal : Math.max(goal, least),
    };
  }

  // One tap opens every member: the camera frames them all, and any still
  // stacked when it lands are listed
  openCluster(cluster) {
    // Anything open holds the pins still; the new tap has to re-cluster.
    this.closePanel();
    this.closePopup();
    this.collapseFan();
    const tapped = new Set(cluster.items.map(memberId));
    const view = this.openView(cluster);
    this.rememberJump(view.zoom);
    this.zoomToCluster(cluster, view, () => {
      if (!this.listMode) {
        // Card mode never opens the panel: partners that share one point fan out.
        this.spreadStacks(tapped, cluster.items.length);
        return;
      }
      const stacked = [...(this.markerByKey || new Map()).values()]
        .map(({ el }) => el.querySelector?.('.br-partner-map__gcluster') || el)
        // A shared-point fan already opens in place, so only zoomable stacks count.
        .filter((el) => el.__members && !el.closest('.br-partner-map__gfan')
          && el.__members.filter((p) => tapped.has(memberId(p))).length > 1);
      if (!stacked.length) return;
      stacked[0].setAttribute('aria-controls', this.panelId());
      this.openPanel(cluster.items, stacked[0]);
    });
  }

  // Every stack the tap left behind fans out together, so one tap shows them all.
  spreadStacks(tapped, total) {
    const mine = (items) => items.filter((p) => tapped.has(memberId(p))).length > 1;
    const wraps = [];
    [...(this.markerByKey || new Map()).entries()].forEach(([key, { el, marker }]) => {
      if (el.__spread && mine(el.__fanMembers)) {
        wraps.push(el);
      } else if (el.__cluster && mine(el.__cluster.items)) {
        const cluster = el.__cluster;
        this.dropMarker(marker);
        const wrap = this.buildCluster(cluster, { fan: true });
        this.markerByKey.set(key, {
          el: wrap,
          marker: this.placeMarker(wrap, cluster.lat, cluster.lng),
          spot: { lat: cluster.lat, lng: cluster.lng },
        });
        wraps.push(wrap);
      }
    });
    if (!wraps.length) return;
    this.indexMarkers();
    wraps.forEach((wrap) => wrap.__spread());
    this.announce(`Showing all ${total} partners in this area.`);
    // Keyboard users land on the first partner; a finger or mouse keeps its place.
    const first = wraps[0].querySelector('.br-partner-map__gfan-items button');
    if (first && this.lastInput === 'key') whenAttached(first, () => first.focus({ preventScroll: true }));
  }

  zoomToCluster(cluster, view, landed) {
    const steps = Math.abs(view.zoom - this.zoom());
    const duration = Math.round(Math.min(900, 400 + 130 * Math.max(0, steps - 1)));
    if (this.engine === 'google') {
      // setZoom snaps, so the camera is tweened by hand.
      this.glideGoogle(view, this.reduceMotion ? 0 : duration, landed);
      this.userMoved = true;
      this.announce(`Zoomed to ${cluster.items.length} partners.`);
      return;
    }
    // The timeout lets the moveend re-cluster land before this reads it.
    if (landed) this.map.once('moveend', () => setTimeout(landed, 0));
    if (this.reduceMotion) {
      this.map.jumpTo({ center: [view.lng, view.lat], zoom: view.zoom });
    } else {
      // Straight to the goal: one continuous move, and longer moves get a
      // little longer.
      this.map.easeTo({
        center: [view.lng, view.lat],
        zoom: view.zoom,
        duration,
        easing: EASE,
      });
    }
    this.userMoved = true;
    this.announce(`Zoomed to ${cluster.items.length} partners.`);
  }

  // Google's animated easeTo, stepped by hand; clusters re-form once it lands.
  glideGoogle(view, duration, landed, { render = true } = {}) {
    const from = this.map.getCenter();
    const z0 = this.zoom();
    const x0 = from.lng();
    const y0 = mercatorY(from.lat());
    const y1 = mercatorY(view.lat);
    if (!duration) {
      this.map.moveCamera({ center: { lat: view.lat, lng: view.lng }, zoom: view.zoom });
      if (render) this.render(this.allPartners);
      landed?.();
      return;
    }
    this.stopGlide();
    this.gliding = true;
    this.glideTarget = view;
    // Zoom about the one point that stays put on screen, as Google Maps does;
    // a near-flat move has no such point, so it pans in a straight line.
    const S = 2 ** (view.zoom - z0);
    const pivot = Math.abs(S - 1) > 0.05
      ? { x: (view.lng * S - x0) / (S - 1), y: (y1 * S - y0) / (S - 1) }
      : null;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const k = EASE(t);
      const z = z0 + (view.zoom - z0) * k;
      const s = 2 ** (z - z0);
      const x = pivot ? pivot.x + (x0 - pivot.x) / s : x0 + (view.lng - x0) * k;
      const y = pivot ? pivot.y + (y0 - pivot.y) / s : y0 + (y1 - y0) * k;
      this.map.moveCamera({ center: { lat: mercatorLat(y), lng: x }, zoom: z });
      if (t < 1) {
        this.glide = requestAnimationFrame(step);
        return;
      }
      this.gliding = false;
      if (render) this.render(this.allPartners);
      landed?.();
    };
    this.glide = requestAnimationFrame(step);
  }

  stopGlide() {
    cancelAnimationFrame(this.glide);
    this.gliding = false;
  }

  /* ---------- Fan mode: one tap un-clusters in place ---------- */

  buildFan(cluster, btn) {
    const wrap = document.createElement('div');
    wrap.className = 'br-partner-map__gfan';
    wrap.appendChild(btn);

    const id = `br-fan-${this.dataset.sectionId || 'map'}-${Math.round(cluster.lat * 1e4)}-${Math.round(cluster.lng * 1e4)}`;
    const items = document.createElement('div');
    items.className = 'br-partner-map__gfan-items';
    items.id = id;
    items.hidden = true;

    cluster.items.forEach((partner) => {
      items.appendChild(this.pinButton(partner, 'br-partner-map__gpin br-partner-map__gfan-item'));
    });
    wrap.appendChild(items);

    btn.setAttribute('aria-controls', id);
    wrap.__fanMembers = cluster.items;
    wrap.__anchor = { lat: cluster.lat, lng: cluster.lng };
    const expand = () => {
      wrap.classList.add('br-partner-map__gfan--open');
      items.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      this.raise(wrap, 10);
      this.layoutFans();
    };
    wrap.__openFan = () => {
      if (this.openFan === wrap) return;
      this.collapseFan();
      this.closePopup();
      this.openFan = wrap;
      expand();
      this.announce(`Showing ${cluster.items.length} partners in this area.`);
      items.querySelector('button')?.focus();
    };
    // Opens alongside other fans, after a cluster tap; focus is the caller's.
    wrap.__spread = () => {
      if (this.openFan === wrap || this.spreadFans.includes(wrap)) return;
      this.spreadFans.push(wrap);
      expand();
    };
    this.bindTap(btn, () => {
      if (this.openFan === wrap || this.spreadFans.includes(wrap)) {
        this.collapseFan();
        return;
      }
      wrap.__openFan();
    });

    return wrap;
  }

  // Every open fan is laid out together, each member as near its own place as
  // the pins around it allow
  layoutFans() {
    const open = [this.openFan, ...this.spreadFans].filter(Boolean);
    if (!open.length) return;
    const movable = [];
    open.forEach((wrap) => {
      const a = this.screenOf(wrap.__anchor);
      wrap.__layout = [];
      wrap.__fanMembers.forEach((partner, i) => {
        const at = this.screenOf(partner);
        movable.push({ wrap, i, partner, ax: a.x, ay: a.y, x: at.x, y: at.y });
      });
    });
    const fixed = [...(this.markerByKey || new Map()).values()]
      .filter(({ el, spot }) => spot && !open.includes(el))
      .map(({ spot }) => this.screenOf(spot));

    // Tips stay far enough inside the canvas for the whole pin to show
    const box = this.canvasEl.getBoundingClientRect();
    const half = 20.5 * this.pinScale() + 4;
    const bounds = { left: half, right: box.width - half, top: 58 * this.pinScale() + 4, bottom: box.height - 4 };
    declutter(movable, fixed, PIN_GAP * this.pinScale(), bounds);

    movable.forEach(({ wrap, i, x, y, ax, ay }) => {
      wrap.__layout[i] = { x: x - ax, y: y - ay };
      const pin = wrap.querySelectorAll('.br-partner-map__gfan-item')[i];
      pin?.style.setProperty('--fan-x', `${(x - ax).toFixed(1)}px`);
      pin?.style.setProperty('--fan-y', `${(y - ay).toFixed(1)}px`);
    });
  }

  collapseFan() {
    const open = [this.openFan, ...this.spreadFans].filter(Boolean);
    this.openFan = null;
    this.spreadFans = [];
    open.forEach((wrap) => this.shutFan(wrap));
  }

  shutFan(wrap) {
    wrap.classList.remove('br-partner-map__gfan--open');
    const items = wrap.querySelector('.br-partner-map__gfan-items');
    if (items) items.hidden = true;
    wrap.querySelector('.br-partner-map__gcluster')?.setAttribute('aria-expanded', 'false');
    this.raise(wrap, null);
  }

  // Google keeps stacking order on the marker, MapLibre on the element.
  raise(el, z) {
    const marker = el.__marker;
    if (this.engine === 'google' && marker) marker.zIndex = z === null ? undefined : z;
    else el.style.zIndex = z === null ? '' : String(z);
  }

  /* ---------- Centring a tapped partner ---------- */

  needsCentre(partner) {
    const box = this.canvasEl.getBoundingClientRect();
    const at = this.screenOf(partner);
    if (!at) return false;
    return Math.hypot(at.x - box.width / 2, at.y - box.height / 2) > 6;
  }

  screenOf(partner) {
    if (this.engine === 'google') {
      const pt = this.projector?.getProjection()
        ?.fromLatLngToContainerPixel(new google.maps.LatLng(partner.lat, partner.lng));
      if (pt) return { x: pt.x, y: pt.y };
      const c = this.map.getCenter();
      const scale = 2 ** this.zoom() * this.tileSize() / 256;
      const box = this.canvasEl.getBoundingClientRect();
      const toX = (lng) => ((lng + 180) / 360) * 256 * scale;
      const toY = (lat) => (0.5 - mercatorY(lat) / (2 * Math.PI)) * 256 * scale;
      return { x: box.width / 2 + toX(partner.lng) - toX(c.lng()), y: box.height / 2 + toY(partner.lat) - toY(c.lat()) };
    }
    return this.map.project([partner.lng, partner.lat]);
  }

  // The camera glides the pin to the middle at the same zoom; the card opens when it lands.
  centreOn(partner, landed) {
    this.centering = true;
    this.userMoved = true;
    const done = () => { this.centering = false; landed(); };
    const duration = this.reduceMotion ? 0 : 420;
    if (this.engine === 'google') {
      this.glideGoogle({ lat: partner.lat, lng: partner.lng, zoom: this.zoom() }, duration, done, { render: false });
      return;
    }
    this.map.once('moveend', () => setTimeout(done, 0));
    if (duration) this.map.easeTo({ center: [partner.lng, partner.lat], duration, easing: EASE });
    else this.map.jumpTo({ center: [partner.lng, partner.lat] });
  }



  closePopup() {
    if (!this.popup) return;
    if (this.engine === 'google') this.popup.setMap(null);
    else this.popup.remove();
    this.popup = null;
    (this.markerEls || []).forEach((el) => {
      if (el.classList.contains('br-partner-map__gcluster')) return;
      el.setAttribute('aria-expanded', 'false');
    });
  }

  openPartner(partner, trigger) {
    if (!this.centering && this.needsCentre(partner)) {
      this.closePopup();
      this.centreOn(partner, () => { if (trigger.isConnected) this.openPartner(partner, trigger); });
      return;
    }
    this.closePopup();
    trigger.setAttribute('aria-expanded', 'true');

    const card = this.buildCard(partner, () => {
      this.closePopup();
      trigger.focus();
    });

    let container;
    // Focus the card, not its close button, which is hidden at rest.
    const place = () => {
      if (!card.isConnected) return;
      this.positionCard(trigger, container);
      card.focus({ preventScroll: true });
    };

    if (this.engine === 'google') {
      if (!this.Popup) this.Popup = buildPopupClass();
      this.popup = new this.Popup(new google.maps.LatLng(partner.lat, partner.lng), card, place);
      container = this.popup.container;
      this.popup.setMap(this.map);
    } else {
      // closeOnClick would fire on the very click that opened this popup, since
      // the marker's click reaches the map too. Closing is wired up below instead.
      const popup = new maplibregl.Popup({
        className: 'br-partner-map__popup',
        closeButton: false,
        closeOnClick: false,
        // Pinned to bottom-centre. Left to itself MapLibre re-anchors near an
        // edge — anchor-right aligns the card's right edge to the pin — and
        // positionCard's offsets, which assume centred, then fight it 262px
        // wide. Keeping the card on canvas is our job, not the engine's.
        anchor: 'bottom',
        offset: 62,
        maxWidth: '369px',
        focusAfterOpen: false,
      })
        .setLngLat([partner.lng, partner.lat])
        .setDOMContent(card)
        .addTo(this.map);

      container = popup.getElement();

      const onMapClick = () => { this.closePopup(); trigger.focus(); };
      popup.on('close', () => {
        trigger.setAttribute('aria-expanded', 'false');
        this.map.off('click', onMapClick);
        if (this.popup === popup) this.popup = null;
      });
      // A tick later, so the click that opened this popup cannot also close it.
      setTimeout(() => { if (this.popup === popup) this.map.once('click', onMapClick); }, 0);
      this.popup = popup;
    }

    if (this.engine !== 'google') requestAnimationFrame(place);

    this.popupTrigger = trigger;
    this.popupContainer = container;
    // Any tap off the card and its pin closes it, page included.
    if (!this.onPageTap) {
      this.onPageTap = (e) => {
        if (!this.popup || this.popupContainer?.contains(e.target) || this.popupTrigger?.contains(e.target)) return;
        if (this.canvasEl.contains(e.target) && e.target.closest('.br-partner-map__gpin, .br-partner-map__gcluster')) return;
        this.closePopup();
      };
      document.addEventListener('pointerdown', this.onPageTap, true);
    }
    this.announce(`${partner.name}, ${placeOf(partner)}. ${partner.category}.`);
  }

  // The card travels out to the pin it belongs to, then back inside the canvas
  // — the camera does not move to make room. Measured, never derived from
  // --fan-x: neither engine rests the card exactly centred on the coordinate,
  // so arithmetic lands it a dozen pixels out. `translate` rather than
  // `transform`: both engines own that.
  positionCard(trigger, container) {
    if (!container) return;
    container.style.translate = '';
    const rest = container.getBoundingClientRect();
    const pin = trigger.getBoundingClientRect();
    const box = this.canvasEl.getBoundingClientRect();

    let dx = (pin.left + pin.width / 2) - (rest.left + rest.width / 2);
    // The card always sits clear above the pin, never over it.
    const dy = (pin.top - CARD_GAP) - rest.bottom;

    if (rest.left + dx < box.left + 8) dx += (box.left + 8) - (rest.left + dx);
    else if (rest.right + dx > box.right - 8) dx -= (rest.right + dx) - (box.right - 8);

    container.style.translate = `${Math.round(dx)}px ${Math.round(dy)}px`;

    // No room above the pin: move the map, never the card over its own pin.
    const short = (box.top + 8) - (rest.top + dy);
    if (short > 0) {
      if (this.engine === 'google') this.map.panBy(0, -short);
      else this.map.panBy([0, -short], { duration: this.reduceMotion ? 0 : 300 });
    }
  }

  buildCard(partner, onClose) {
    const card = document.createElement('div');
    card.className = 'br-partner-map__gcard';
    card.tabIndex = -1;

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'br-partner-map__card-close';
    close.innerHTML =
      '<span class="visually-hidden"></span>'
      + '<svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true" focusable="false">'
      + '<path d="M1 1 9 9M9 1 1 9" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
    close.querySelector('.visually-hidden').textContent = `Close ${partner.name}`;
    close.addEventListener('click', onClose);
    card.appendChild(close);

    this.fillPartner(card, partner);
    return card;
  }

  /* ---------- The panel: one surface, always in the same seat ---------- */

  panelId() {
    return `br-partner-panel-${this.dataset.sectionId || 'map'}`;
  }

  // Built once and reused, so the panel keeps its seat rather than being
  // rebuilt somewhere new on every tap.
  panel() {
    if (this.panelEl) return this.panelEl;

    const panel = document.createElement('div');
    panel.className = 'br-partner-map__panel';
    panel.id = this.panelId();
    panel.hidden = true;

    const head = document.createElement('div');
    head.className = 'br-partner-map__panel-head';

    const title = document.createElement('h3');
    title.className = 'br-partner-map__panel-title';
    title.tabIndex = -1;
    head.appendChild(title);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'br-partner-map__panel-close';
    const label = document.createElement('span');
    label.className = 'visually-hidden';
    label.textContent = 'Close the partner list';
    close.appendChild(label);
    close.insertAdjacentHTML('beforeend',
      '<svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true" focusable="false">'
      + '<path d="M1 1 9 9M9 1 1 9" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>');
    close.addEventListener('click', () => {
      const trigger = this.panelTrigger;
      this.closePanel();
      trigger?.focus();
    });
    head.appendChild(close);
    panel.appendChild(head);

    const list = document.createElement('ul');
    list.className = 'br-partner-map__panel-list';
    list.setAttribute('role', 'list');
    panel.appendChild(list);

    // Inside the stage, not the canvas: the engine owns everything under the
    // canvas and rebuilds it whenever the camera moves.
    (this.querySelector('.br-partner-map__stage') || this).appendChild(panel);
    this.panelEl = panel;
    this.panelTitleEl = title;
    this.panelListEl = list;
    return panel;
  }

  openPanel(partners, trigger) {
    const panel = this.panel();
    this.markPins('false');

    this.panelTitleEl.textContent = partners.length === 1
      ? partners[0].name
      : `${partners.length} partners in this area`;

    this.panelListEl.replaceChildren(...partners.map((p) => this.buildEntry(p)));
    this.panelListEl.scrollTop = 0;
    panel.hidden = false;
    this.panelOpen = true;
    this.panelTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    this.panelTitleEl.focus();

    this.announce(partners.length === 1
      ? `${partners[0].name}, ${placeOf(partners[0])}. ${partners[0].category}.`
      : `${partners.length} partners in this area. ${partners.map((p) => p.name).join(', ')}.`);
  }

  closePanel() {
    if (!this.panelOpen) return;
    this.panelOpen = false;
    if (this.panelEl) this.panelEl.hidden = true;
    if (this.panelListEl) this.panelListEl.replaceChildren();
    this.markPins('false');
    this.panelTrigger = null;
  }

  markPins(state) {
    (this.markerEls || []).forEach((el) => el.setAttribute('aria-expanded', state));
  }

  // One row per partner
  buildEntry(partner) {
    const row = document.createElement('li');
    row.className = 'br-partner-map__entry';
    this.fillPartner(row, partner);
    return row;
  }

  // The logo tile and text column both surfaces share, so the card and the
  // list row can never drift apart.
  fillPartner(host, partner) {
    // The white tile is always drawn, logo or not, so a sourced-but-logoless
    // partner keeps the design's shape.
    const wrap = document.createElement('span');
    wrap.className = 'br-partner-map__logo';
    if (partner.logo) {
      const img = document.createElement('img');
      img.src = partner.logo;
      img.alt = '';
      img.width = 78;
      img.height = 66;
      img.loading = 'lazy';
      img.decoding = 'async';
      // A missing file would otherwise paint the browser's broken-image glyph.
      img.addEventListener('error', () => img.remove());
      wrap.appendChild(img);
    }
    host.appendChild(wrap);

    const body = document.createElement('span');
    body.className = 'br-partner-map__card-body';
    body.appendChild(span('br-partner-map__card-name', partner.name));
    body.appendChild(span('br-partner-map__card-cat', partner.category));

    if (partner.address) body.appendChild(span('br-partner-map__card-street', partner.address));
    const foot = document.createElement('span');
    foot.className = 'br-partner-map__card-foot';
    foot.appendChild(span('br-partner-map__card-city', partner.city));
    if (partner.url) {
      const link = document.createElement('a');
      link.className = 'br-partner-map__card-link';
      link.href = partner.url;
      link.textContent = 'Website';
      const sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = `, ${partner.name}`;
      link.appendChild(sr);
      link.rel = 'noopener';
      link.target = '_blank';
      foot.appendChild(link);
    }
    body.appendChild(foot);
    host.appendChild(body);
    return host;
  }

  /* ---------- City labels (MapLibre only; Google draws its own) ---------- */

  // Map furniture, not content: labels are inert, unfocusable and hidden from
  // assistive tech, and they thin out as the map zooms out.
  async addCityLabels(map) {
    if (!this.dataset.citiesUrl) return;
    let cities;
    try {
      cities = await (await fetch(this.dataset.citiesUrl)).json();
    } catch (err) {
      return console.warn('[br-partner-map] city labels skipped:', err);
    }

    this.cityMarkers = cities.map((c) => {
      const el = document.createElement('span');
      el.className = 'br-partner-map__city';
      el.dataset.rank = String(c.rank);
      el.setAttribute('aria-hidden', 'true');
      el.textContent = c.name;
      return new maplibregl.Marker({ element: el, anchor: 'left' })
        .setLngLat([c.lng, c.lat])
        .addTo(map);
    });

    this.cityMarkers.forEach((m) => {
      const el = m.getElement();
      el.dataset.w = String(el.offsetWidth || 60);
    });
    this.refreshCityLabels();
  }

  // Labels thin out by rank as the map zooms out, and any label a partner pin
  // is sitting on is dropped outright — the pin is the content, the label is not.
  refreshCityLabels() {
    if (!this.cityMarkers || !this.map || this.engine !== 'maplibre') return;
    const z = this.map.getZoom();
    const limit = z < 3 ? 1 : z < 4 ? 2 : z < 5 ? 3 : 4;

    const pins = (this.markers || []).map((m) => this.map.project(m.getLngLat()));
    this.cityMarkers.forEach((m) => {
      const el = m.getElement();
      if (Number(el.dataset.rank) > limit) { el.hidden = true; return; }
      const p = this.map.project(m.getLngLat());
      // The label runs rightwards from its anchor. The marker anchors at its
      // tip, so the 41x58 teardrop occupies q.y-58 to q.y — upwards, not down.
      const w = Number(el.dataset.w) || 60;
      el.hidden = pins.some((q) => (
        p.x < q.x + 24 && p.x + w > q.x - 24 && p.y > q.y - 66 && p.y < q.y + 10
      ));
    });
  }
}

function span(className, text) {
  const el = document.createElement('span');
  el.className = className;
  el.textContent = text || '';
  return el;
}

// Loads the Google Maps JS API once, however many maps are on the page.
// `marker` is the library that carries AdvancedMarkerElement.
function loadGoogleMaps(key) {
  if (window.google && window.google.maps && window.google.maps.marker) return Promise.resolve();
  if (window.__brGoogleMapsLoading) return window.__brGoogleMapsLoading;

  window.__brGoogleMapsLoading = new Promise((resolve, reject) => {
    const callback = '__brGoogleMapsReady';
    window[callback] = () => resolve();
    const script = document.createElement('script');
    script.src = 'https://maps.googleapis.com/maps/api/js'
      + `?key=${encodeURIComponent(key)}`
      + '&v=weekly&libraries=marker&loading=async'
      + `&callback=${callback}`;
    script.async = true;
    script.onerror = () => reject(new Error('Google Maps JS API could not load.'));
    document.head.appendChild(script);
  });
  return window.__brGoogleMapsLoading;
}

function brandStyle(host) {
  const cs = getComputedStyle(host);
  const token = (name, fallback) => (cs.getPropertyValue(name).trim() || fallback);

  const land = token('--map-land', '#d7faec');
  const border = token('--map-border', '#a6dcfe');
  const borderOpacity = parseFloat(token('--map-border-opacity', '0.55')) || 0.55;
  const lineWidth = ['interpolate', ['linear'], ['zoom'], 2, 0.6, 8, 1.6];

  // No glyphs key: there are no text layers, and null is not a legal value.
  return {
    version: 8,
    sources: {
      countries: { type: 'geojson', data: host.dataset.countriesUrl },
      regions: { type: 'geojson', data: host.dataset.regionsUrl },
      ...(host.dataset.lakesUrl ? { lakes: { type: 'geojson', data: host.dataset.lakesUrl } } : {}),
      ...(host.dataset.riversUrl ? { rivers: { type: 'geojson', data: host.dataset.riversUrl } } : {}),
    },
    layers: [
      { id: 'water', type: 'background', paint: { 'background-color': token('--map-water', '#a6dcfe') } },
      { id: 'land', type: 'fill', source: 'countries', paint: { 'fill-color': land } },
      ...(host.dataset.lakesUrl ? [{
        id: 'lakes', type: 'fill', source: 'lakes',
        paint: { 'fill-color': token('--map-water', '#a6dcfe') },
      }] : []),
      ...(host.dataset.riversUrl ? [{
        id: 'rivers',
        type: 'line',
        source: 'rivers',
        paint: {
          'line-color': token('--map-river', '#a6dcfe'),
          'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.5, 8, 1.4],
          'line-opacity': 0.9,
        },
      }] : []),
      {
        id: 'region-lines',
        type: 'line',
        source: 'regions',
        paint: { 'line-color': border, 'line-width': lineWidth, 'line-opacity': borderOpacity },
      },
      {
        id: 'country-lines',
        type: 'line',
        source: 'countries',
        paint: { 'line-color': border, 'line-width': lineWidth, 'line-opacity': borderOpacity },
      },
    ],
  };
}

// Loads the vendored engine once, however many maps are on the page.
function loadMapLibre(jsUrl, cssUrl) {
  if (window.maplibregl) return Promise.resolve();
  if (window.__brMapLibreLoading) return window.__brMapLibreLoading;

  window.__brMapLibreLoading = new Promise((resolve, reject) => {
    if (cssUrl && !document.querySelector(`link[href="${cssUrl}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = jsUrl;
    script.onload = () => (window.maplibregl ? resolve() : reject(new Error('maplibregl missing after load')));
    script.onerror = () => reject(new Error('MapLibre GL JS could not load.'));
    document.head.appendChild(script);
  });
  return window.__brMapLibreLoading;
}

// Closest two fanned pins may sit, centre to centre: wider than the 41px pin,
// so every member of a fan stays its own target.
const PIN_GAP = 46;

// Rings filled from the inside out
// Pushes fanned pins apart until none covers another, from their true screen
// points: partners on one coordinate start round a ring just wide enough,
// everyone else starts exactly where they are. Fixed pins only push, and a
// partner whose place is on screen stays on screen.
function declutter(movable, fixed, gap, bounds) {
  const inside = (m) => bounds && m.x >= bounds.left && m.x <= bounds.right
    && m.y >= bounds.top && m.y <= bounds.bottom;
  movable.forEach((m) => { m.keep = inside(m); });
  // Partners within half a pin of each other share one ring, not just exact twins
  const groups = [];
  movable.forEach((m) => {
    const near = groups.filter((g) => g.some((o) => Math.hypot(o.x - m.x, o.y - m.y) < gap / 2));
    const merged = near.flat().concat(m);
    near.forEach((g) => groups.splice(groups.indexOf(g), 1));
    groups.push(merged);
  });
  groups.forEach((group) => {
    const n = group.length;
    if (n < 2) return;
    const cx = group.reduce((s, m) => s + m.x, 0) / n;
    const cy = group.reduce((s, m) => s + m.y, 0) / n;
    const r = gap / (2 * Math.sin(Math.PI / n));
    // A pair stands side by side; more start with one straight up
    const from = n === 2 ? Math.PI : -Math.PI / 2;
    group.sort((p, q) => Math.atan2(p.y - cy, p.x - cx) - Math.atan2(q.y - cy, q.x - cx));
    group.forEach((m, i) => {
      const a = from + (2 * Math.PI * i) / n;
      m.x = cx + r * Math.cos(a);
      m.y = cy + r * Math.sin(a);
    });
  });

  const apart = (a, b, share) => {
    let dx = a.x - b.x;
    let dy = a.y - b.y;
    let d = Math.hypot(dx, dy);
    if (d >= gap) return false;
    if (d < 0.01) { dx = 1; dy = 0; d = 1; }
    const push = (gap - d + 0.5) * share;
    a.x += (dx / d) * push;
    a.y += (dy / d) * push;
    return true;
  };
  for (let pass = 0; pass < 80; pass += 1) {
    let moved = false;
    for (let i = 0; i < movable.length; i += 1) {
      for (let j = i + 1; j < movable.length; j += 1) {
        const a = movable[i];
        const b = movable[j];
        const ax = a.x;
        const ay = a.y;
        if (apart(a, b, 0.5)) {
          moved = true;
          b.x -= a.x - ax;
          b.y -= a.y - ay;
        }
      }
      fixed.forEach((f) => { if (apart(movable[i], f, 1)) moved = true; });
    }
    movable.forEach((m) => {
      if (!m.keep) return;
      const x = Math.min(bounds.right, Math.max(bounds.left, m.x));
      const y = Math.min(bounds.bottom, Math.max(bounds.top, m.y));
      if (Math.abs(x - m.x) > 0.5 || Math.abs(y - m.y) > 0.5) moved = true;
      m.x = x;
      m.y = y;
    });
    if (!moved) break;
  }
}

function buildPopupClass() {
  if (window.__brPartnerPopupV2) return window.__brPartnerPopupV2;

  class PartnerPopup extends google.maps.OverlayView {
    constructor(position, content, onFirstDraw) {
      super();
      this.position = position;
      this.onFirstDraw = onFirstDraw;
      this.container = document.createElement('div');
      this.container.className = 'br-partner-map__gpopup';
      this.container.appendChild(content);
    }

    onAdd() { this.getPanes().floatPane.appendChild(this.container); }

    onRemove() { this.container.remove(); }

    draw() {
      const point = this.getProjection().fromLatLngToDivPixel(this.position);
      if (!point) return;
      this.container.style.left = `${point.x}px`;
      this.container.style.top = `${point.y}px`;
      // The card is only in the page once drawn, so it is placed from here.
      const done = this.onFirstDraw;
      this.onFirstDraw = null;
      done?.();
    }
  }

  window.__brPartnerPopupV2 = PartnerPopup;
  return PartnerPopup;
}

// Google attaches a new marker a frame or more later; measuring before that reads 0,0.
function whenAttached(el, fn, tries = 20) {
  if (el.isConnected) fn();
  else if (tries > 0) requestAnimationFrame(() => whenAttached(el, fn, tries - 1));
}

// Web Mercator, and back again
function mercatorY(lat) {
  return Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
}

function mercatorLat(y) {
  return (360 / Math.PI) * (Math.atan(Math.exp(y)) - Math.PI / 4);
}

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Easing for the camera, as a function the engine can call. Newton-Raphson on
// x, then evaluate y — the standard solve.
//
// Deliberately NOT the theme's UI curve. cubic-bezier(0.32, 0.72, 0, 1) is
// right for a 41px pin and wrong for a whole viewport: it puts 27% of the move
// in the first tenth of the time and 95% by halfway, with a peak per-frame step
// of 13.7% of the distance — a lurch, then dead time. MapLibre's default `ease`
// has the opposite fault, a lazy 9% in the first tenth before it commits.
// (0.22, 0.61, 0.36, 1) starts immediately and decelerates evenly: 27 / 59 / 87
// per cent at a tenth, a quarter and half the duration, peak step 9.2%.
function cubicBezier(x1, y1, x2, y2) {
  const a = (u, v) => 1 - 3 * v + 3 * u;
  const b = (u, v) => 3 * v - 6 * u;
  const c = (u) => 3 * u;
  const at = (t, u, v) => ((a(u, v) * t + b(u, v)) * t + c(u)) * t;
  const slope = (t, u, v) => 3 * a(u, v) * t * t + 2 * b(u, v) * t + c(u);

  return (x) => {
    let t = x;
    for (let i = 0; i < 5; i += 1) {
      const d = slope(t, x1, x2);
      if (d === 0) break;
      t -= (at(t, x1, x2) - x) / d;
    }
    return at(t, y1, y2);
  };
}

const EASE = cubicBezier(0.22, 0.61, 0.36, 1);

// One partner, identified by the things that place it on the map.
// The screen point a marker stands on: the tip of its teardrop.
function tipOf(el) {
  if (!el || !el.isConnected) return null;
  const r = el.getBoundingClientRect();
  if (!r.width && !r.height) return null;
  return { x: r.left + r.width / 2, y: r.bottom };
}

function memberId(partner) {
  return `${partner.name}|${partner.lat}|${partner.lng}`;
}

// A cluster is the set of partners in it — the key render() diffs against.
function clusterKey(cluster) {
  return cluster.items.map(memberId).sort().join('~');
}

// Partner-to-partner distances never change, and every zoom level asks for them again
const partnerGaps = new WeakMap();
function partnerGap(a, b) {
  let row = partnerGaps.get(a);
  if (!row) partnerGaps.set(a, (row = new Map()));
  let d = row.get(b);
  if (d === undefined) {
    d = haversineMeters(a.lat, a.lng, b.lat, b.lng);
    row.set(b, d);
  }
  return d;
}

// Street and city when the entry has a street, else the city alone
function placeOf(partner) {
  return partner.address ? `${partner.address}, ${partner.city}` : partner.city;
}

// Web Mercator pixel position at a given world width
function worldPx(lat, lng, world) {
  const s = Math.sin((Math.max(-85, Math.min(85, lat)) * Math.PI) / 180);
  return { x: ((lng + 180) / 360) * world, y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * world };
}

function clusterByProximity(partners, box) {
  const dist = (a, b) => haversineMeters(a.lat, a.lng, b.lat, b.lng);
  // Two markers touch when their pin boxes overlap on screen
  const touches = (a, b) => {
    const p = worldPx(a.lat, a.lng, box.world);
    const q = worldPx(b.lat, b.lng, box.world);
    return Math.abs(p.x - q.x) < box.w && Math.abs(p.y - q.y) < box.h;
  };
  const centre = (c) => {
    c.lat = c.items.reduce((s, i) => s + i.lat, 0) / c.items.length;
    c.lng = c.items.reduce((s, i) => s + i.lng, 0) / c.items.length;
  };

  // Closest pair first, so no partner joins a group just because it came first
  // in the list. Distances are kept, and only the merged group's row is redone.
  const clusters = partners.map((p) => ({ lat: p.lat, lng: p.lng, items: [p] }));
  const gaps = partners.map((a) => partners.map((b) => partnerGap(a, b)));
  for (;;) {
    let best = null;
    for (let i = 0; i < clusters.length; i += 1) {
      for (let j = i + 1; j < clusters.length; j += 1) {
        const d = gaps[i][j];
        if ((!best || d < best.d) && touches(clusters[i], clusters[j])) best = { i, j, d };
      }
    }
    if (!best) break;
    const { i, j } = best;
    clusters[i].items = clusters[i].items.concat(clusters[j].items);
    clusters.splice(j, 1);
    gaps.splice(j, 1);
    gaps.forEach((row) => row.splice(j, 1));
    centre(clusters[i]);
    clusters.forEach((c, k) => { gaps[i][k] = dist(clusters[i], c); gaps[k][i] = gaps[i][k]; });
  }

  // The mean can sit in open water, so the pin stands on the member nearest it
  const anchor = (c) => {
    centre(c);
    const mean = { lat: c.lat, lng: c.lng };
    let best = Infinity;
    c.items.forEach((i) => {
      const d = dist(mean, i);
      if (d < best) { best = d; c.pin = i; }
    });
    c.lat = c.pin.lat;
    c.lng = c.pin.lng;
  };
  clusters.forEach(anchor);

  // Every partner joins the pin nearest it; pins that land too close then merge
  for (let round = 0; round < 8; round += 1) {
    let changed = false;
    const homes = clusters.map((c) => c.pin);
    clusters.forEach((c) => { c.next = []; });
    clusters.forEach((c, ci) => c.items.forEach((p) => {
      let to = ci;
      let near = partnerGap(p, homes[ci]);
      homes.forEach((h, hi) => {
        const d = partnerGap(p, h);
        if (d < near - 1) { to = hi; near = d; }
      });
      if (to !== ci) changed = true;
      clusters[to].next.push(p);
    }));
    for (let i = clusters.length - 1; i >= 0; i -= 1) {
      clusters[i].items = clusters[i].next;
      delete clusters[i].next;
      if (!clusters[i].items.length) clusters.splice(i, 1);
      else if (changed) anchor(clusters[i]);
    }
    for (let i = 0; i < clusters.length; i += 1) {
      for (let j = clusters.length - 1; j > i; j -= 1) {
        if (!touches(clusters[i].pin, clusters[j].pin)) continue;
        clusters[i].items = clusters[i].items.concat(clusters[j].items);
        clusters.splice(j, 1);
        anchor(clusters[i]);
        changed = true;
      }
    }
    if (!changed) break;
  }

  // Last word goes to the pins: no two boxes may touch
  for (let merged = true; merged;) {
    merged = false;
    for (let i = 0; i < clusters.length && !merged; i += 1) {
      for (let j = i + 1; j < clusters.length; j += 1) {
        if (!touches(clusters[i].pin, clusters[j].pin)) continue;
        clusters[i].items = clusters[i].items.concat(clusters[j].items);
        clusters.splice(j, 1);
        anchor(clusters[i]);
        merged = true;
        break;
      }
    }
  }
  return clusters;
}

if (!customElements.get('partner-map')) {
  customElements.define('partner-map', PartnerMap);
}
